import type { ReactNode } from 'react';

type Props = {
  /** The type mark — a 12px lucide icon in muted foreground (playlist, schedule, app, …). */
  icon: ReactNode;
  label: string;
  /** A quieter second label after the name, e.g. the app that makes an item need a plan. */
  detail?: string;
};

/**
 * A named object in a sentence's list: type icon, name, optional detail, in a pill that wraps.
 * One shape for every "these things" list — what uses an asset about to be trashed, what a plan
 * refuses — so the same playlist or app looks the same wherever it is named.
 *
 * With a detail, the hidden " - " keeps the text reading "name - detail" to a screen reader and
 * to the paywall e2e that matches on it; the full text is also the hover title, since a long
 * generated name truncates inside the pill.
 */
export function ObjectChip({ icon, label, detail }: Props) {
  return (
    <span
      className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-xs text-foreground"
      title={detail ? `${label} - ${detail}` : label}
    >
      {icon}
      <span className="truncate">{label}</span>
      {detail && (
        <span className="shrink-0 text-muted-foreground">
          <span className="sr-only"> - </span>
          {detail}
        </span>
      )}
    </span>
  );
}
